import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ExternalLink, CheckCircle, Clock, Circle,
  GitBranch, ListChecks, Sparkles, Map, AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import NAVIGATION_STRUCTURE from '../../config/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Module Detail sub-page — /admin/dashboard/modules/:code
 *
 * Shows: Overview, Sub-modules, Progress, Recent Deliveries,
 *        Dependencies, Internal Roadmap, Direct Links.
 */

const STATUS_META = {
  complete: { label: 'Complete', icon: CheckCircle, bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-300', dot: 'bg-emerald-500' },
  in_progress: { label: 'In Progress', icon: Clock, bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-300', dot: 'bg-amber-500' },
  planned: { label: 'Planned', icon: Circle, bg: 'bg-slate-50', text: 'text-slate-500', ring: 'ring-slate-200', dot: 'bg-slate-400' },
};


const ModuleDetail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !code) return;
    setLoading(true);
    axios.get(`${API_URL}/api/command-center/module/${code.toUpperCase()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { setDetail(r.data); setError(null); })
      .catch((err) => setError(err?.response?.data?.detail || 'Failed to load module'))
      .finally(() => setLoading(false));
  }, [code, token]);

  // Locate matching navigation section to render sub-modules
  const navSection = useMemo(() => {
    if (!code) return null;
    return Object.values(NAVIGATION_STRUCTURE).find((s) => s.code === code.toUpperCase());
  }, [code]);

  if (loading) {
    return <div className="p-6 text-sm text-[#6C757D]" data-testid="module-detail-loading">Loading module…</div>;
  }

  if (error || !detail) {
    return (
      <div className="p-6" data-testid="module-detail-error">
        <div className="flex items-center gap-2 text-red-600 mb-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">Module not found</span>
        </div>
        <p className="text-xs text-[#6C757D] mb-4">{error || 'The requested module does not exist.'}</p>
        <Link to="/admin/dashboard" className="text-xs text-[#00B4D8] hover:underline" data-testid="module-detail-back-link">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const meta = STATUS_META[detail.status] || STATUS_META.planned;
  const StatusIcon = meta.icon;
  const enabledModules = navSection ? navSection.modules.filter((m) => m.enabled) : [];
  const plannedModules = navSection ? navSection.modules.filter((m) => !m.enabled) : [];
  const totalModules = (navSection?.modules || []).length;
  const progressPct = totalModules > 0 ? Math.round((enabledModules.length / totalModules) * 100) : 0;

  return (
    <div data-testid="module-detail-page">

      {/* Header */}
      <div className="mb-5">
        <button onClick={() => navigate('/admin/dashboard')}
                data-testid="module-detail-back-btn"
                className="text-xs text-[#6C757D] hover:text-[#023E8A] flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${meta.bg} flex items-center justify-center ring-2 ${meta.ring}`}>
              <StatusIcon className={`w-6 h-6 ${meta.text}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-mono font-bold text-[#2B2D42]">{detail.code}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${meta.bg} ${meta.text}`}>{meta.label}</span>
              </div>
              <h1 className="text-2xl font-bold text-[#2B2D42]">{detail.label}</h1>
            </div>
          </div>
          {totalModules > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase font-bold text-[#6C757D]">Progress</div>
              <div className="text-2xl font-bold text-[#2B2D42] tabular-nums">{enabledModules.length}/{totalModules}</div>
              <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                <div className={meta.dot + ' h-full'} style={{ width: `${progressPct}%` }}></div>
              </div>
            </div>
          )}
        </div>
        <p className="text-sm text-[#6C757D] mt-3 max-w-3xl">{detail.description}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left column — Sub-modules + Direct Links */}
        <div className="lg:col-span-2 space-y-5">
          {enabledModules.length > 0 && (
            <Card data-testid="module-detail-submodules">
              <CardContent className="p-5">
                <SectionTitle icon={ListChecks} title="Sub-modules" count={enabledModules.length} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  {enabledModules.map((m) => {
                    const Icon = m.icon;
                    return (
                      <Link key={m.id}
                            to={m.path}
                            data-testid={`module-submodule-${m.id}`}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-[#00B4D8] hover:bg-[#E0F2FE]/50 text-sm group">
                        {Icon && <Icon className="w-4 h-4 text-[#023E8A] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#2B2D42] truncate">{m.label}</div>
                          <div className="text-[10px] text-[#6C757D] truncate">{m.description}</div>
                        </div>
                        <ExternalLink className="w-3 h-3 text-[#6C757D] group-hover:text-[#00B4D8]" />
                      </Link>
                    );
                  })}
                </div>

                {plannedModules.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="text-[10px] uppercase font-bold text-[#6C757D] mb-2">Planned Sub-modules</div>
                    <div className="flex flex-wrap gap-1.5">
                      {plannedModules.map((m) => (
                        <span key={m.id}
                              data-testid={`module-planned-${m.id}`}
                              className="text-[11px] px-2 py-1 rounded bg-slate-100 text-[#6C757D] border border-slate-200">
                          {m.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recent Deliveries */}
          <Card data-testid="module-detail-deliveries">
            <CardContent className="p-5">
              <SectionTitle icon={Sparkles} title="Recent Deliveries" count={detail.recent_deliveries?.length || 0} />
              {(detail.recent_deliveries || []).length === 0 ? (
                <p className="text-xs text-[#6C757D] mt-3">No deliveries recorded yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100 mt-3">
                  {detail.recent_deliveries.map((d, idx) => (
                    <li key={idx} className="flex items-center gap-3 py-2" data-testid={`module-delivery-${idx}`}>
                      <span className="text-[10px] font-mono text-[#6C757D] w-20 flex-shrink-0">{d.date}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#2B2D42] truncate">{d.title}</div>
                        <div className="text-[11px] text-[#6C757D] truncate">{d.scope}</div>
                      </div>
                      {d.link && (
                        <Link to={d.link} className="text-[#00B4D8]" title="Open">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column — Dependencies, Roadmap, Direct Links */}
        <div className="space-y-5">
          <Card data-testid="module-detail-dependencies">
            <CardContent className="p-5">
              <SectionTitle icon={GitBranch} title="Dependencies" count={(detail.dependencies || []).length} />
              {(detail.dependencies || []).length === 0 ? (
                <p className="text-xs text-[#6C757D] mt-3">No upstream dependencies.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {detail.dependencies.map((dep) => (
                    <Link key={dep}
                          to={`/admin/dashboard/modules/${dep}`}
                          data-testid={`module-dependency-${dep}`}
                          className="text-[11px] font-mono font-bold px-2 py-1 rounded bg-[#023E8A]/10 text-[#023E8A] hover:bg-[#023E8A] hover:text-white transition-colors">
                      {dep}
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="module-detail-roadmap">
            <CardContent className="p-5">
              <SectionTitle icon={Map} title="Internal Roadmap" count={(detail.roadmap || []).length} />
              {(detail.roadmap || []).length === 0 ? (
                <p className="text-xs text-[#6C757D] mt-3">No backlog items.</p>
              ) : (
                <ul className="mt-3 space-y-1.5">
                  {detail.roadmap.map((r, idx) => (
                    <li key={idx} className="text-xs text-[#2B2D42] flex items-start gap-2" data-testid={`module-roadmap-${idx}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] mt-1.5 flex-shrink-0"></span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card data-testid="module-detail-links">
            <CardContent className="p-5">
              <SectionTitle icon={ExternalLink} title="Direct Links" count={(detail.links || []).length} />
              <div className="space-y-1 mt-3">
                {(detail.links || []).map((l, idx) => (
                  <Link key={idx}
                        to={l.path}
                        data-testid={`module-link-${idx}`}
                        className="flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-slate-50 text-xs text-[#023E8A]">
                    <span>{l.label}</span>
                    <ArrowLeft className="w-3 h-3 rotate-180" />
                  </Link>
                ))}
                {(detail.links || []).length === 0 && (
                  <p className="text-xs text-[#6C757D]">No direct links configured.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


const SectionTitle = ({ icon: Icon, title, count }) => (
  <div className="flex items-center gap-2">
    {Icon && <Icon className="w-4 h-4 text-[#FF6B35]" />}
    <h3 className="text-xs font-bold uppercase tracking-wide text-[#2B2D42]">{title}</h3>
    {count !== undefined && (
      <span className="text-[10px] text-[#6C757D] bg-slate-100 px-1.5 py-0.5 rounded">{count}</span>
    )}
  </div>
);


export default ModuleDetail;
